import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { isCancel } from "axios";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useErrorSnackbar } from "@/hooks/useErrorSnackbar";
import axiosInstance from "@/utils/networkWrapper";
import type { RequestDetailFields } from "./activityLogGroupedDisplay";

/** One raw row inside an expanded group. Mirrors `ActivityLogGroupRow` (`backend/models/team.py`). */
export interface ActivityLogGroupRow {
	id: string;
	tableName: string;
	/** The business object `tableName` represents ("Nurse Shift", "PTO", "Request", ...) — a backend
	 * closed-set ID, same "treated as an ID, not display text" convention as `GroupedActivityLogEntry.what`
	 * (see `OBJECT_TYPE_LABEL_KEY` in `activityLogGroupedDisplay.ts`). Falls back to the raw table name,
	 * unbounded, for a table the backend's own map doesn't cover. */
	objectType: string;
	changedAt: string;
	/** `Optional[str] = Field(alias="changedUser", default=None)` on the backend serializes as JSON
	 * `null` (no `exclude_none`), not an omitted key — so this must be `string | null`, not `string |
	 * undefined`, same null-vs-undefined convention already applied to `details` below. */
	changedUser: string | null;
	/** `changedUser`'s first name — same rule as `ResolvedGroupedActivityLogEntry.performedByFirstName`.
	 * Optional because this bundle can reach a backend that predates the field. */
	changedUserFirstName?: string | null;
	changeType: string;
	/** `Optional[Dict[str, Any]] = Field(alias="changes", default=None)` — same null-vs-undefined shape
	 * as `changedUser`/`details`: serializes as JSON `null` when unset, never an omitted key. */
	changes: Record<string, unknown> | null;
	/** `reason: Optional[str] = None` — same null-vs-undefined shape as `changedUser` above. */
	reason: string | null;
	/** This row's OWN api_request_id — distinct from the enclosing group's `apiRequestId` for a
	 * lineage-sourced row pulled in from another transaction. Lets the detail grid tell own-transaction
	 * rows apart from lineage rows, and copy a lineage row's own request id (see `GroupDetailPanel.tsx`). */
	apiRequestId: string;
	/**
	 * Row-specific extras this row's own `changes` doesn't carry — populated ONLY for a
	 * `requests_status`-table row (raw ISO shift bounds, joined back via `request_id` since a bare
	 * `requests_status` write has no job/shift context of its own). `null` for every other table's row.
	 * Typed as the shared `RequestDetailFields` shape (2026-09-06 fix, replacing the raw
	 * `Record<string, unknown>` this field used to be — mirrors the backend's own
	 * `RequestStatusRowDetails`, `backend/models/team.py`) — this is the SAME shape
	 * `formatRequestDetailSublabel`'s other callers already use, reused here rather than declaring a
	 * second, duplicate interface. `Optional[Model] = Field(default=None)` with no `exclude_none`
	 * serializes an unset value as JSON `null`, not an omitted key, same null-vs-undefined convention
	 * already applied to `GroupedActivityLogEntry.details`.
	 */
	details: RequestDetailFields | null;
	/** A closed-set ID (2026-09-07) — same "backend computes the ID, frontend translates/composes it"
	 * convention as `objectType`/`actionName`/`channel`/`what` — naming which, if any, of a small set of
	 * business-sentence templates this row's "Details" column should compose instead of the generic
	 * `[objectType, reason, sublabel]` fragments (see `rowWhatText` in `GroupDetailPanel.tsx`, and
	 * `ROW_WHAT_CHIP_LABEL_KEY`/`ROW_WHAT_STATUS_VERB_LABEL_KEY`/`ROW_WHAT_OBJECT_VERB_LABEL_KEY` in
	 * `activityLogGroupedDisplay.ts` for the closed set of values). `null` for the large majority of
	 * rows/tables this doesn't (yet) cover. Same null-vs-undefined shape as `changedUser` above. */
	rowWhat: string | null;
	/** This row's own raw `nurse` column (2026-09-06 fix) — same field/meaning as
	 * `GroupedActivityLogEntry.performedFor`'s own fallback for every tier except a `requests`-table row
	 * (that table always writes `nurse` NULL at write time — a known, consistent-with-existing-behavior
	 * limitation, not specially resolved here). Same null-vs-undefined shape as `changedUser` above. */
	performedFor: number | null;
	/** Same field/meaning as `GroupedActivityLogEntry.performedForName` — a hard-deleted employee's name,
	 * since `performedFor` above is necessarily null for that row. */
	performedForName: string | null;
}

/** Mirrors `ActivityLogGroupDetail` (`backend/models/team.py`). */
export interface ActivityLogGroupDetail {
	rows: ActivityLogGroupRow[];
	/** The group's real row count — `rows.length < totalCount` is the only time "load all" applies. */
	totalCount: number;
}

const ACTIVITY_LOG_GROUP_DETAIL_QUERY_KEY = "activity-log-group-detail";

/**
 * The ONE place this key is built — `useResolveActivityLogGroupMutation` seeds this cache directly.
 * Both sites must use this builder: a one-element mismatch is neither a type nor a runtime error, the
 * seeded entry just lives under a key nobody reads and the optimization silently does nothing.
 */
export const activityLogGroupDetailQueryKey = (departmentId: string | undefined, apiRequestId: string | undefined, full: boolean, includePunches: boolean) =>
	[ACTIVITY_LOG_GROUP_DETAIL_QUERY_KEY, departmentId, apiRequestId, full, includePunches] as const;

/**
 * True for an aborted request (axios `CanceledError` / `ERR_CANCELED`) rather than a real failure. A
 * cancel is an outcome, not an error: it must never raise a snackbar or `trackError`. Exported so the
 * guard is unit-testable without mounting the panel.
 */
export const isRequestCancellation = (error: unknown): boolean =>
	isCancel(error) || (error instanceof Error && "code" in error && (error as { code?: unknown }).code === "ERR_CANCELED");

/**
 * Lazy per-row detail fetch for one expanded group. `full` is a key dimension so the uncapped read
 * caches separately and the capped rows stay on screen while it downloads. `enabled` is required, not
 * defaulted: the panel runs two instances and a silent `true` would fire the expensive one for every
 * expanded row. The `queryFn` consumes `signal`, so `cancelQueries` aborts the request, not just the
 * promise — and collapsing a row mid-fetch aborts too, via last-observer cancellation.
 */
export const useActivityLogGroupDetailQuery = (
	apiRequestId: string | undefined,
	full: boolean,
	includePunches: boolean,
	enabled: boolean,
): UseQueryResult<ActivityLogGroupDetail, Error> => {
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();

	return useQuery({
		queryKey: activityLogGroupDetailQueryKey(chosenDepartment?.department_id, apiRequestId, full, includePunches),
		queryFn: async ({ signal }) => {
			// `enabled` already gates on both, but TS can't see that — assert rather than cast, so a future
			// caller that drops the gate fails loudly instead of requesting `.../groups/undefined`.
			if (!apiRequestId) throw new Error("useActivityLogGroupDetailQuery ran without an apiRequestId");
			track("activity_log_group_detail_fetch_start", { department_id: chosenDepartment?.department_id, api_request_id: apiRequestId, full });

			const { data } = await axiosInstance.get<ActivityLogGroupDetail>(`/activity-log/departments/${chosenDepartment.department_id}/groups/${apiRequestId}`, {
				params: { full, include_punches: includePunches },
				// TanStack's own controller, so cancelling the query tears down the HTTP request with it.
				signal,
			});

			track("activity_log_group_detail_fetch_success", {
				department_id: chosenDepartment?.department_id,
				api_request_id: apiRequestId,
				full,
				row_count: data.rows.length,
				total_count: data.totalCount,
			});
			return data;
		},
		staleTime: 60 * 1000,
		throwOnError: false,
		enabled: enabled && !!chosenDepartment?.department_id && !!apiRequestId,
		meta: {
			onError: (error: unknown) => {
				// A cancel is a user decision, not a failure. query-core swallows its own `CancelledError`;
				// this covers the axios one too, so the exclusion holds however the rejection is routed.
				if (isRequestCancellation(error)) return;
				showErrorSnackbar(t("activityLog.grouped.detail.error"), { action: "failed to fetch activity log group detail" }, error);
				trackError("activity_log_group_detail_fetch_error", {
					error_message: (error as Error)?.message,
					department_id: chosenDepartment?.department_id,
					api_request_id: apiRequestId,
					full,
					error_source: "API",
				});
			},
		},
	});
};
